import React, { useEffect, useState } from "react";
import H5 from "components/typography/H5";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge, Button } from "theme-ui";
import { themevals } from "theme/themevals";
import { getInvoicePayments, deletePayment } from "api/Invoices";
import TableBasic from "components/ui/Table";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment-timezone";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import { EllipsisIcon, PlusIcon, CheckMarkIcon, PaymentsIcon, TrashIcon, QuestionMarkIcon } from "components/svg";
import BasicButton from "components/forms/BasicButton";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import SelectInput from "components/forms/SelectInput";
import TextInput from "components/forms/TextInput";
import SelectDate from "components/forms/SelectDate";
import { createPayment, updatePayment } from "api/Invoices";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import { useToast } from "components/toast";

const Payments = ({ isCompany, invoice, reloadInvoice }: { isCompany: boolean; invoice: any; reloadInvoice: () => void }) => {
  const columnHelper = createColumnHelper();
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [canOpenPaymentEdit, setCanOpenPaymentEdit] = useState<boolean>(false);
  const toast = useToast();

  const _openPaymentEdit = (payment: any) => {
    setSelectedPayment(payment);
    setCanOpenPaymentEdit(true);
  };

  const _cancelConfirmModal = () => {
    setConfirmDeleteModalOpen(false);
    setSelectedPayment(null);
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Invoice Payments";
    _init();
  }, []);

  useEffect(() => {
    if (selectedPayment && canOpenPaymentEdit) {
      setIsEditOpen(true);
      setCanOpenPaymentEdit(false);
    }
  }, [selectedPayment, canOpenPaymentEdit]);

  const _init = async () => {
    const res = await getInvoicePayments({ data: { id: invoice?.id } });
    if (res.items) {
      setPayments(res.items);
    }
  };

  const manualPaymentOptions = [
    { label: "Cash", value: "CASH" },
    { label: "Check", value: "CHECK" },
    { label: "ACH", value: "ACH" },
  ];

  /**
   * The picker hands back local midnight as unix seconds. Today keeps
   * the current time, which is what the form always sent. Any other day
   * is sent as NOON local, with its offset: midnight sits four hours
   * from the UTC day boundary, and a payment that slides into the day
   * before lands in the wrong period on the dashboard's Collected
   * figure, which filters on this column.
   */
  const toTransactionDate = (unixSeconds: number) => {
    const picked = moment.unix(unixSeconds);
    if (picked.isSame(moment(), "day")) return moment().format();
    return picked.clone().hour(12).minute(0).second(0).format();
  };

  const todayEnd = moment().endOf("day").unix();

  const _confirmDeletePayment = (payment: any) => {
    setSelectedPayment(payment);
    setConfirmDeleteModalOpen(true);
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.transactionDate, {
      id: "transactionDate",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Transaction Date</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.value, {
      id: "amount",
      cell: (info: any) => {
        const value = info.getValue();
        return <Box sx={{ color: themevals.colors.black }}>{value?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? "$0.00"}</Box>;
      },
      header: () => <span>Base Amount</span>,
    }),
    columnHelper.accessor((row: any) => row?.fees, {
      id: "fess",
      cell: (info: any) => {
        const value = info.getValue();
        return <Box sx={{ color: themevals.colors.black }}>{value?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? "$0.00"}</Box>;
      },
      header: () => <span>Fees</span>,
    }),
    // Insert the Total Amount column after Fees
    columnHelper.accessor((row: any) => (row?.value ?? 0) + (row?.fees ?? 0), {
      id: "totalAmount",
      cell: (info: any) => {
        const value = info.getValue();
        return <Box sx={{ color: themevals.colors.black }}>{value?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? "$0.00"}</Box>;
      },
      header: () => <span>Total Amount</span>,
    }),
    columnHelper.accessor((row: any) => row?.paymentMethod, {
      id: "method",
      cell: (info: any) => info.getValue(),
      header: () => <span>Method</span>,
    }),
    columnHelper.accessor((row: any) => row?.reference, {
      id: "reference",
      cell: (info: any) => info.getValue(),
      header: () => <span>Reference</span>,
    }),
    columnHelper.accessor((row: any) => row?.createdAt, {
      id: "createdAt",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Date Added</span>,
    }),
    columnHelper.accessor((row: any) => {
      if (row?.paymentMethod === "Zoho_Payment") {
        return "System Generated";
      }
      return `${row?.createdBy?.firstName} ${row?.createdBy?.lastName}`;
    }, {
      id: "createdBy",
      cell: (info: any) => info.getValue(),
      header: () => <span>Added By</span>,
    }),
    ...(isCompany
      ? [
          columnHelper.accessor("actions", {
            id: "actions",
            cell: (info: any) => {
              const paymentMethod = info.row.original.paymentMethod;
              if (paymentMethod === "CREDIT_CARD" || paymentMethod === "PLAID_ACH") {
                return null;
              }
              const items: { label: string; onClick: () => void; style?: React.CSSProperties }[] = [
                { label: "Edit", onClick: () => _openPaymentEdit(info.row.original) },
                { label: "Delete", onClick: () => _confirmDeletePayment(info.row.original.id), style: { color: "var(--theme-ui-colors-danger)" } },
              ];
              return (
                <Box>
                  <DropDownMenu toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />} items={items} />
                </Box>
              );
            },
            header: () => null,
            enableSorting: false,
            meta: {
              style: { width: "10px" },
            },
          }),
        ]
      : []),
  ];

  const _submitManualPayment = async (values: any) => {
    setIsSubmitting(true);
    const payment = {
      paymentMethod: values.method,
      value: values.amount,
      type: "INVOICE",
      reference: values.reference,
      transactionDate: toTransactionDate(values.transactionDate ?? moment().startOf("day").unix()),
      payer: { id: invoice.client.id },
      source: "manual",
    };

    const res = await createPayment({ id: invoice.id, data: payment });
    if (res) {
      _init();
      setIsOpen(false);
      setIsSubmitting(false);
      reloadInvoice();
    }
  };

  const _submitEditManualPayment = async (values: any) => {
    const update: Record<string, any> = {
      payer: { id: invoice.client.id },
      paymentMethod: values.method,
      value: values.amount,
      type: "INVOICE",
      reference: values.reference,
    };
    // Only when the DAY changed. Re-sending an untouched date would
    // rewrite its time to noon for nothing.
    const originalDay = selectedPayment?.transactionDate
      ? moment(selectedPayment.transactionDate).startOf("day").unix()
      : null;
    if (values.transactionDate && values.transactionDate !== originalDay) {
      update.transactionDate = toTransactionDate(values.transactionDate);
    }

    const res = await updatePayment({ data: { id: invoice.id, paymentId: selectedPayment.id }, update: update });

    if (res.payment?.id) {
      setIsEditOpen(false);
      setSelectedPayment(null);
      toast.add(`Payment updated.`, "var(--theme-ui-colors-success)");
      _init();
      reloadInvoice();
    }
  };

  const _deletePayment = async () => {
    setSubmitting(true);
    const res = await deletePayment({ data: { id: invoice.id, paymentId: selectedPayment } });
    if (res.success) {
      setSubmitting(false);
      setConfirmDeleteModalOpen(false);
      toast.add(`Payment deleted.`, "var(--theme-ui-colors-success)");
      _init();
      reloadInvoice();
    } else {
      setSubmitting(false);
      setConfirmDeleteModalOpen(false);
      toast.add(`Error deleting payment.`, "var(--theme-ui-colors-danger)");
    }
    setSelectedPayment(null);
  };

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Payments
      </H5>
      {isCompany && (
        <Flex sx={{ justifyContent: "flex-end" }}>
          <BasicButton scheme="secondaryOutline" onClick={() => setIsOpen(!isOpen)}>
            <PlusIcon fill={"#000"} />
            {"Add payment"}
          </BasicButton>
        </Flex>
      )}
      <Flex sx={{ width: "100%", marginTop: "30px" }}>
        {payments && payments.length > 0 && (
          <TableBasic data={payments} columns={columns} enablePaging={false} enableSearch={false} customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }} disableSorting />
        )}
        {payments && payments.length === 0 && (
          <Flex sx={{ width: "100%", justifyContent: "center", alignItems: "center" }}>
            <Label sx={{ color: themevals.colors.dkgrey, fontSize: "14px", fontWeight: 500, fontFamily: "Poppins" }}>There are no payments on this invoice.</Label>
          </Flex>
        )}
      </Flex>
      <Modal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} centered closeOnBackdropClick={false}>
        <Formik initialValues={{ transactionDate: moment().startOf("day").unix() }} onSubmit={(values) => _submitManualPayment(values)}>
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            submitCount,
          }: {
            isSubmitting: boolean;
            submitCount: number;
            values: any;
            setFieldValue: (name: string, val?: any) => void;
            errors: any;
          }) => {
            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => setIsOpen(false)}>
                  <ModalIcon>
                    <PaymentsIcon fill={"000"} />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Add payment</ModalTitle>
                    <ModalSubtitle>{"Add a payment to an Invoice."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <SelectInput
                    name="method"
                    label="Method"
                    value={values.method}
                    options={manualPaymentOptions}
                    onChange={(val: any) => setFieldValue("method", val)}
                    $errors={errors.method && submitCount > 0 ? errors.method : null}
                    $customStyles={{ width: "100%", marginBottom: "30px" }}
                  />
                  <TextInput
                    name={`amount`}
                    value={values.amount}
                    type="number"
                    label="Amount"
                    prefix={"$"}
                    $customStyles={{ marginBottom: "30px" }}
                    onChange={(val: any) => setFieldValue(`amount`, val)}
                    $errors={errors?.amount && submitCount > 0 ? errors?.amount : null}
                  />
                  <TextInput
                    name={`reference`}
                    value={values.reference}
                    label="Reference"
                    $customStyles={{ marginBottom: "30px" }}
                    onChange={(val: any) => setFieldValue(`reference`, val)}
                    $errors={errors?.reference && submitCount > 0 ? errors?.reference : null}
                  />
                  <SelectDate
                    name="transactionDate"
                    label="Payment date"
                    value={values.transactionDate}
                    maxDate={todayEnd}
                    usePortal
                    onChange={(val: any) => setFieldValue("transactionDate", val)}
                    $customStyles={{ marginBottom: "30px" }}
                  />
                </ModalBody>
                <ModalFooter>
                  <BasicButton type="button" onClick={() => setIsOpen(false)} scheme="secondaryOutline">
                    Cancel
                  </BasicButton>
                  <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true} styles={{ marginLeft: "10px" }}>
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}Save Payment
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
      <Modal isOpen={isEditOpen} toggle={() => setIsEditOpen(!isEditOpen)} centered closeOnBackdropClick={false}>
        <Formik
          initialValues={{
            method: selectedPayment?.paymentMethod,
            amount: selectedPayment?.value,
            reference: selectedPayment?.reference,
            transactionDate: selectedPayment?.transactionDate ? moment(selectedPayment.transactionDate).startOf("day").unix() : undefined,
          }}
          onSubmit={(values) => _submitEditManualPayment(values)}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            submitCount,
          }: {
            isSubmitting: boolean;
            submitCount: number;
            values: any;
            setFieldValue: (name: string, val?: any) => void;
            errors: any;
          }) => {
            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => setIsEditOpen(false)}>
                  <ModalIcon>
                    <PaymentsIcon fill={"000"} />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Edit payment</ModalTitle>
                    <ModalSubtitle>{"Edit a payment on an Invoice."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <SelectInput
                    name="method"
                    label="Method"
                    value={values.method}
                    options={manualPaymentOptions}
                    onChange={(val: any) => setFieldValue("method", val)}
                    $errors={errors.method && submitCount > 0 ? errors.method : null}
                    $customStyles={{ width: "100%", marginBottom: "30px" }}
                  />
                  <TextInput
                    name={`amount`}
                    value={values.amount}
                    type="number"
                    label="Amount"
                    prefix={"$"}
                    $customStyles={{ marginBottom: "30px" }}
                    onChange={(val: any) => setFieldValue(`amount`, val)}
                    $errors={errors?.amount && submitCount > 0 ? errors?.amount : null}
                  />
                  <TextInput
                    name={`reference`}
                    value={values.reference}
                    label="Reference"
                    $customStyles={{ marginBottom: "30px" }}
                    onChange={(val: any) => setFieldValue(`reference`, val)}
                    $errors={errors?.reference && submitCount > 0 ? errors?.reference : null}
                  />
                  <SelectDate
                    name="transactionDate"
                    label="Payment date"
                    value={values.transactionDate}
                    maxDate={todayEnd}
                    usePortal
                    onChange={(val: any) => setFieldValue("transactionDate", val)}
                    $customStyles={{ marginBottom: "30px" }}
                  />
                </ModalBody>
                <ModalFooter>
                  <BasicButton type="button" onClick={() => setIsEditOpen(false)} scheme="secondaryOutline">
                    Cancel
                  </BasicButton>
                  <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true} styles={{ marginLeft: "10px" }}>
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}Save Payment
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
      {confirmDeleteModalOpen && (
        <BasicModal
          title={`Delete Payment?`}
          cancel_click={() => _cancelConfirmModal()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => _deletePayment()}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <TrashIcon fill={"#fff"} />}
              {"Delete Payment"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to delete the payment from the invoice?</P>
        </BasicModal>
      )}
    </>
  );
};

export default Payments;
